<template>
  <div
    v-click-outside="hideSingleSelectItems"
    class="singleselect-box"
  >
    <button
      class="singleselect-box__select"
      :disabled="disabled"
      :class="{
        'singleselect-box__select--disabled': disabled,
        'singleselect-box__select--active': selectActive && !showErrorMessage,
        'singleselect-box__select--error': showErrorMessage,
        'singleselect-box__select--none-label': selectedItemsLabel === noDataText,
        'singleselect-box__select--clear': clear,
      }"
      :style="{
        'max-width': maxWidth ? `${maxWidth}px` : 'auto',
      }"
      type="button"
      @click="selectActive = !selectActive"
    >
      <tooltipped-text :text="selectedItemsLabel" />
      <v-spacer v-if="!clear" />
      <v-chip
        v-if="displayCountChipByDefault || (showCountChip && Object.keys(selectedItems).length)"
        small
        :color="shouldShowCountChipWithoutSelection ? 'transparent' : 'primaryLight'"
        :text-color="shouldShowCountChipWithoutSelection ? 'transparent' : 'primaryWhite'"
        @mouseover="hoverOverChip('over')"
        @mouseleave="hoverOverChip('leave')"
      >
        <span v-if="showRemoveIcon">
          <v-icon
            size="22"
            class="ml-n1"
            color="white"
            @click.stop="clearSelection"
          >$icon_close</v-icon>
        </span>
        <span v-else>{{ SELECTED_COUNT }}</span>
      </v-chip>
      <v-icon
        v-show="!showErrorMessage"
        :size="24"
        class="ml-auto"
        :color="disabled ? 'secondaryLight' : 'secondaryMedium'"
      >
        $icon_arrow_down
      </v-icon>
      <v-icon
        v-show="showErrorMessage"
        :size="24"
        color="negative"
        class="ml-auto"
      >
        $icon_attention
      </v-icon>
      <v-progress-linear
        v-if="loading"
        absolute
        bottom
        indeterminate
        rounded
        height="2"
        :color="loaderColor"
      />
    </button>
    <div
      v-show="selectActive"
      ref="singleSelectBoxContentRef"
      class="singleselect-box__content"
      :style="{
        width: menuProps?.maxWidth ? `${menuProps.maxWidth}` : 'auto',
      }"
    >
      <div
        v-if="originalItems.length || showSearchBoxOnNoItems"
        class="mb-2"
      >
        <SearchBar
          v-model="currentSearch"
          :collections="[]"
          :icon="searchIcon"
          :show-collection="false"
          :placeholder="'Filter'"
        />
      </div>
      <div class="singleselect-box__items">
        <div v-if="!loading">
          <div>
            <div
              v-if="Object.values(selectedItems).length && selectedItems?.value"
              class="singleselect-box__item--selected"
            >
              <tooltipped-text
                :text="selectedItems?.text"
                :max-width="labelMaxWidth"
              />
            </div>
            <v-divider
              v-show="Object.values(selectedItems).length && selectedItems?.value"
              class="my-1 mr-1"
            />
            <div v-if="searchResultItems">
              <div
                v-for="item in searchResultItems"
                :key="`${item.text}${item.value}`"
                class="singleselect-box__item--searched"
                @click="clickItem(item)"
              >
                <tooltipped-text
                  :text="item.text"
                  :max-width="labelMaxWidth"
                />
              </div>
            </div>
          </div>
          <div
            v-if="currentSearch && searchResultItems.length === 0 && !isSelectedItemSearched"
            class="singleselect-box__no-results"
          >
            <p>{{ noDataText }}</p>
          </div>
          <div
            v-if="!currentSearch && originalItems.length === 0 && !props.collections?.length"
            class="singleselect-box__no-results"
          >
            <p>{{ noDataText }}</p>
          </div>
          <div
            v-for="item in availableCollections"
            :key="`${item.text}${item.value}`"
            class="singleselect-box__item--normal"
            @click="clickItem(item)"
          >
            <tooltipped-text :text="item.text" />
          </div>
        </div>
        <loading-spinner
          v-if="loading"
          is-loading
          :absolute="false"
          class="search-loader"
        />
        <div
          v-if="paginated && !loading"
          data-testid="eewc-single-select-search-pagination"
          :class="['pagination-wrapper', { 'pagination-wrapper--no-height': paginationRef.completed }]"
        >
          <div
            v-intersect="handleIntersection"
            data-testid="eewc-single-select-search-intersection"
          />
          <loading-spinner
            v-if="!paginationRef.completed || paginationRef.loaded"
            is-loading
            :absolute="false"
            :class="['infinite-loader', { 'infinite-loader--margin': !selectedItems }]"
          />
        </div>
      </div>
    </div>
    <div
      v-show="showErrorMessage"
      class="singleselect-box__error-message"
    >
      {{ errorMessage || errorBucket[0] }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';

import { IntersectionPaginationOption, IntersectionStateChanger } from '@/utils/types';
import LoadingSpinner from '../../components/loaders/LoadingSpinner.vue';
import TooltippedText from '../tooltip/TooltippedText/TooltippedText.vue';
import SearchBar from '../text-inputs/searchBar/SearchBar.vue';

import { Item, SelectBoxItem } from './types';
import type { MenuProps } from './AutoComplete.vue';

const props = withDefaults(
  defineProps<{
    displayCountChipByDefault?: boolean;
    disabled?: boolean;
    value?: SelectBoxItem;
    maxWidth?: number;
    collections: Item[];
    errorMessage?: string;
    labelMaxWidth?: string | number;
    loading?: boolean;
    paginated?: boolean;
    clear?: boolean;
    showCountChip?: boolean;
    showSearchBoxOnNoItems?: boolean;
    searchIcon?: string;
    rules?: Array<string | boolean | ((value: object) => boolean | string)>;
    labelText?: string;
    noDataText?: string;
    menuProps?: MenuProps;
  }>(),
  {
    displayCountChipByDefault: false,
    value: {},
    maxWidth: undefined,
    labelMaxWidth: '100%',
    errorMessage: '',
    loading: false,
    paginated: false,
    clear: false,
    showCountChip: false,
    showSearchBoxOnNoItems: false,
    searchIcon: '$icon_search',
  }
);

const emit = defineEmits<{
  (e: 'input', value?: SelectBoxItem): void;
  (e: 'click-outside'): void;
  (
    e: 'search',
    value: {
      searchText: string | undefined | null;
      state: IntersectionStateChanger;
    }
  ): void;
  (e: 'infinite', value: IntersectionStateChanger): void;
  (e: 'update-dropdown-rect', value: DOMRectReadOnly | null): void;
}>();

const paginationRef = ref<IntersectionPaginationOption>({
  loaded: false,
  completed: false,
  bounceTimer: undefined,
});

const singleSelectBoxContentRef = ref<HTMLDivElement | null>(null);

const loaderColor = computed(() => (props.errorMessage ? 'negative' : 'secondaryMedium'));
const SELECTED_COUNT = 1;

// CONVERTED PROPS
const originalItems = computed(() => {
  return props.collections;
});

// SELECTED ITEMS
const selectedItems = computed({
  get: () => (props.value ? props.value : {}),
  set: (newValue) => {
    emit('input', newValue);
    validate(newValue);
  },
});

const shouldShowCountChipWithoutSelection = computed(
  () => props.displayCountChipByDefault && !Object.keys(selectedItems.value)?.length
);

const availableCollections = computed(() => {
  if (currentSearch.value) {
    return props.collections.filter(
      (collection: Item) => !collection?.text?.toLowerCase()?.includes(currentSearch.value?.toLowerCase())
    );
  }

  if (selectedItems.value?.value) {
    return props.collections.filter((collection: Item) => collection?.value !== selectedItems.value.value);
  }

  return props.collections;
});

// AVAILABLE ITEMS
const availableItems = computed(() => {
  const availableItemsList = props.collections.flatMap((collection) => collection);

  const remainingAvailableItems = availableItemsList.filter((item) => selectedItems.value?.value !== item.value);

  return remainingAvailableItems;
});

// SEARCH
const currentSearchLower = computed(() => currentSearch.value.toLowerCase());

const searchResultItems = computed(() => {
  return availableItems.value.filter((item) => {
    const itemName = item.text.toLowerCase();

    return currentSearchLower.value ? itemName.indexOf(currentSearchLower.value) > -1 : false;
  });
});

const isSelectedItemSearched = computed(() => {
  const selectedItemText = selectedItems.value?.text?.toLowerCase();

  return currentSearchLower.value && selectedItemText?.includes(currentSearchLower.value);
});

// SELECT ALL
const selectActive = ref<boolean>(false);

const currentSearch = ref<string>('');
// LABEL
const selectedItemsLabel = computed(() => {
  if (props.showCountChip) {
    return props.labelText;
  } else if (selectedItems.value.value) {
    return selectedItems.value.text;
  } else {
    return props.labelText;
  }
});

// EVENT HANDLERS
const hideSingleSelectItems = () => {
  selectActive.value = false;
  emit('click-outside');
};

const handleIntersection = (
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver,
  isIntersecting: boolean
) => {
  if (isIntersecting) {
    if (paginationRef.value.completed) return;
    paginationRef.value.loaded = false;
    emit('infinite', state);
  }
};

// VALIDATION
const isDirty = ref<boolean>(false);
const valid = ref<boolean>(false);
const errorBucket = ref<string[]>([]);
const showRemoveIcon = ref<boolean>(false);

const showErrorMessage = computed(() => {
  if (props.rules?.length) {
    return isDirty.value && !valid.value;
  } else {
    return !!props.errorMessage;
  }
});

function validate(newValue: SelectBoxItem) {
  isDirty.value = true;

  if (!props.rules?.length) {
    return;
  }

  const value = newValue || props?.value;
  const errorBucketTemp = [];

  for (let index = 0; index < props.rules.length; index++) {
    const rule = props.rules[index];
    const valid = typeof rule === 'function' ? rule(value) : rule;

    if (valid === false || typeof valid === 'string') {
      errorBucketTemp.push(valid || '');
    }
  }

  errorBucket.value = errorBucketTemp;
  valid.value = errorBucketTemp.length === 0;

  return valid.value;
}

// Function responsible for removing/adding items allFilteredItems
const clickItem = (item: SelectBoxItem) => {
  if (item.value === selectedItems.value.value) {
    selectedItems.value = {};
  } else {
    selectedItems.value = item;
  }
};

const hoverOverChip = (type: string) => {
  type === 'over' ? (showRemoveIcon.value = true) : (showRemoveIcon.value = false);
};

const clearSelection = () => {
  if (Object.keys(selectedItems.value)?.length) {
    selectedItems.value = {};
    currentSearch.value = '';
    hoverOverChip('leave');
  } else if (props.displayCountChipByDefault) {
    selectActive.value = !selectActive.value;
  }
};

watch(selectActive, (newValue, oldValue) => {
  if (!newValue && newValue !== oldValue) {
    currentSearch.value = '';
  }

  nextTick(() => {
    emit('update-dropdown-rect', singleSelectBoxContentRef.value?.getBoundingClientRect() ?? null);
  });
});

watch(
  () => currentSearchLower.value,
  (newVal) => {
    // debounce for the search
    clearTimeout(paginationRef.value.bounceTimer);
    paginationRef.value.bounceTimer = setTimeout(() => {
      emit('search', { searchText: newVal, state });
    }, 500);
  }
);

const state: IntersectionStateChanger = {
  loaded: () => {
    paginationRef.value.loaded = true;
  },
  completed: () => {
    paginationRef.value.completed = true;
  },
  reset: () => {
    paginationRef.value.loaded = false;
    paginationRef.value.completed = false;
  },
};

defineExpose({ validate });
</script>

<style lang="scss" scoped>
@import '../../assets/styles/main';
@import './styles/singleSelect';
.singleselect-box {
  &__select {
    position: relative;

    &--clear {
      border: none;
      background: none;
      box-shadow: none !important;
    }
  }

  &__item {
    height: 28px;
    &__checkbox {
      padding-top: 2px !important;
    }

    &--selected {
      padding: 3px;
    }

    &--normal {
      padding: 3px;

      &:hover {
        background: $accentClear;
        border-radius: 4px;
        margin-right: 4px;
        cursor: pointer;
      }
    }

    &--searched {
      padding: 3px;
      border-radius: 4px;
      margin-right: 4px;

      &:hover {
        background: $accentClear;
        border-radius: 4px;
        margin-right: 4px;
      }
    }
  }
  &__searched-items {
    background: $accentClear;
    box-shadow: none;
    border-radius: 4px;
  }

  &__category {
    @include body-3;
    color: $secondaryMedium;
    margin: 4px 0 !important;
  }

  &__no-results {
    display: flex;
    align-items: center;
    height: 44px;
    p {
      @include body-2;
      margin: 0 !important;
    }
  }
}

.pagination-wrapper {
  height: 60px;
  display: grid;
  place-items: center;

  &--no-height {
    height: auto;
  }

  .infinite-loader {
    text-align: center;

    &--margin {
      margin-top: -30px;
    }
  }
}

.search-loader {
  height: 60px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.v-chip {
  @include subtitle-3;
  margin-left: 12px;
  margin-right: 10px;
  max-width: 37px;
  min-width: 37px;
  display: flex;
  justify-content: center;
}
</style>
